import {
    Box,
    Button as ChakraButton,
    Flex,
    Text,
    useToast,
} from '@chakra-ui/react';
import { EventType, People } from 'types/models';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@chakra-ui/modal';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import {
    getWorkloadInHours,
    renderDateForPresence,
    renderDateWithTime,
} from 'utils/dateUtils';

import COLORS from 'constants/COLORS';
import CustomButton from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import LoadingSpinner from 'components/LoadingSpinner';
import { PageSubtitle } from 'custom-style-components';
import PropTypes from 'prop-types';
import { api } from 'services/axios';
import axios from 'axios';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyUsersFromEvent } from 'services/fetch/users';
import getActivityCode from 'utils/getActivityCode';
import { maskCpf } from 'utils/maskCpf';
import renderEventName from 'utils/renderEventName';

interface ShowParticipatingUsersProps {
    modalOpen: boolean;
    setModalOpen: (param: boolean) => void;
    event: EventType;
    // Props mantidos para compatibilidade com o pai, mas ignorados internamente
    loading?: boolean;
    error?: string | null;
    users?: People[];
}

const ShowParticipatingUsers: React.FC<ShowParticipatingUsersProps> = ({
    modalOpen,
    setModalOpen,
    event,
}) => {
    const toast = useToast();
    const [internalUsers, setInternalUsers] = useState<People[]>([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalError, setInternalError] = useState<string | null>(null);

    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 3;

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filter, setFilter] = useState<number>(0);

    const [downloading, setDownloading] = useState(false);

    // Debounce na pesquisa
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Reseta para a primeira página ao pesquisar
        }, 400);

        return () => clearTimeout(handler);
    }, [search]);

    // Busca de participantes paginada e filtrada
    useEffect(() => {
        if (!modalOpen || !event?.id) return;

        setInternalLoading(true);
        setInternalError(null);

        const cancelSource = createCancelTokenSource();

        fetchManyUsersFromEvent(event.id, cancelSource.token, {
            page,
            limit,
            search: debouncedSearch || undefined,
            minWorkload: filter > 0 ? filter : undefined,
        })
            .then(({ users: loadedUsers, totalCount: loadedCount }) => {
                setInternalUsers(loadedUsers);
                setTotalCount(loadedCount);
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setInternalError(err.message || 'Ocorreu um erro ao carregar participantes');
            })
            .finally(() => {
                setInternalLoading(false);
            });

        return () => {
            cancelSource.cancel();
        };
    }, [modalOpen, event?.id, page, debouncedSearch, filter]);

    // Reseta paginação ao abrir o modal para outro evento
    useEffect(() => {
        if (modalOpen) {
            setPage(1);
            setSearch('');
            setFilter(0);
        }
    }, [modalOpen, event?.id]);

    const calculateUserHours = (user: any): number => {
        return getWorkloadInHours(user.totalWorkload || 0);
    };

    const handleDownloadReport = async (detailed: boolean) => {
        if (!event?.id) return;
        console.log(event);
        console.log('aq');
        setDownloading(true);

        try {
            const response = await api.get(`/user/event/${event.id}/report`, {
                params: {
                    detailed,
                    search: debouncedSearch || undefined,
                    minWorkload: filter > 0 ? filter : undefined,
                },
                responseType: 'blob',
                timeout: 60000,
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const eventNameStr = renderEventName(event);
            const workloadStr = filter > 0 ? ` - horas min ${filter}` : '';
            const searchStr = debouncedSearch ? ` - busca ${debouncedSearch}` : '';
            const typeStr = detailed ? 'detalhado' : 'resumo';
            const filename = `${eventNameStr}${workloadStr}${searchStr} - ${typeStr}.pdf`;
            const sanitizedFilename = filename.replace(/[\\/:*?"<>|]/g, '_');

            link.setAttribute('download', sanitizedFilename);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: 'Relatório baixado com sucesso',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: 'Erro ao baixar relatório',
                description: err instanceof Error ? err.message : 'Erro inesperado',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setDownloading(false);
        }
    };

    const renderParticipatingUsers = (
        users: People[]
    ): ReactElement[] | string => {
        if (users.length > 0) {
            return users.map((user) => {
                const totalHours = calculateUserHours(user);
                return (
                    <div key={user.cpf} style={{ marginBottom: '2.4rem' }}>
                        <PageSubtitle style={{ fontSize: 20 }}>
                            {user.name} ({user.cpf && maskCpf(user.cpf)}) -{' '}
                            <span style={{ fontWeight: 'bold', color: '#2b6cb0' }}>
                                {totalHours} horas
                            </span>
                        </PageSubtitle>
                        <PageSubtitle
                            style={{
                                marginLeft: '2.4rem',
                                fontSize: 18,
                                fontWeight: 'normal',
                            }}
                        >
                            {user.managingActivities && user.managingActivities.length > 0 && (
                                <Box mb={2}>
                                    <Text fontWeight="semibold" fontSize="2xl" color="#2D8A3E">
                                        Atividades como Ministrante:
                                    </Text>
                                    {user.managingActivities.map((managingAct, idx) => (
                                        <div key={idx} style={{ marginLeft: '1.2rem', marginBottom: '0.4rem' }}>
                                            • {managingAct.title} ({getWorkloadInHours(managingAct.workloadInMinutes)} horas)
                                        </div>
                                    ))}
                                </Box>
                            )}

                            {user.activityRegistration && user.activityRegistration.length > 0 && (
                                <Box>
                                    <Text fontWeight="semibold" fontSize="2xl" color="#2D8A3E">
                                        Atividades como Aluno / Participante:
                                    </Text>
                                    {user.activityRegistration.map((registry, index) => (
                                        <div
                                            key={index}
                                            style={{ marginBottom: '1.5rem', marginLeft: '1.2rem' }}
                                        >
                                            - {getActivityCode(registry.activity)} - {registry.activity.title}
                                            <div style={{ marginLeft: '2.4rem', fontSize: '15px' }}>
                                                Carga horária: {getWorkloadInHours(registry.activity.workloadInMinutes)} horas
                                                <br />
                                                Inscrição: {renderDateWithTime(registry.registryDate)}
                                                <br />
                                                Presenças ({' '}
                                                <span style={{ fontWeight: 'bold' }}>
                                                    {registry.readyForCertificate ? 'Completo' : 'Incompleto'}
                                                </span>{' '}
                                                )
                                                {registry.presences.map((presence, idx) => (
                                                    <div
                                                        key={idx}
                                                        style={{
                                                            marginLeft: '2.4rem',
                                                            fontSize: '14px',
                                                            color: presence.isPresent ? '#2f855a' : '#c53030',
                                                        }}
                                                    >
                                                        {renderDateForPresence(
                                                            new Date(presence.schedule.startDate),
                                                            presence.schedule.durationInMinutes
                                                        )}{' '}
                                                        - {presence.isPresent ? 'Presente' : 'Ausente'}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </Box>
                            )}
                        </PageSubtitle>
                    </div>
                );
            });
        }
        return 'Não há participantes correspondentes aos filtros neste evento';
    };

    const totalPages = Math.ceil(totalCount / limit);

    return (
        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
            <ModalOverlay />
            <ModalContent maxW="80%" maxH="95%" style={{ overflowY: 'scroll', borderRadius: '12px' }}>
                <ModalHeader borderBottom="1px solid" borderColor="gray.100" py={5}>
                    Participantes do evento {event ? renderEventName(event) : ''}
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody py={6}>
                    {/* Filtros */}
                    <Flex direction={{ base: 'column', md: 'row' }} gap={4} mb={6}>
                        <Box flex={1}>
                            <label htmlFor="search-input" style={{ fontWeight: 'bold', fontSize: '14px', color: '#4a5568' }}>
                                Pesquisar por nome ou CPF
                            </label>
                            <input
                                id="search-input"
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Digite o nome ou CPF..."
                                style={{
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e0',
                                    width: '100%',
                                    fontSize: '15px',
                                    backgroundColor: '#fff',
                                    minHeight: '3rem',
                                    padding: '0 0.8rem',
                                    marginTop: '0.3rem',
                                }}
                            />
                        </Box>
                        <Box w={{ base: '100%', md: '250px' }}>
                            <label htmlFor="workload-filter" style={{ fontWeight: 'bold', fontSize: '14px', color: '#4a5568' }}>
                                Carga horária mínima (h)
                            </label>
                            <input
                                id="workload-filter"
                                type="number"
                                min="0"
                                value={filter === 0 ? '' : filter.toString()}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    setFilter(isNaN(val) ? 0 : val);
                                }}
                                placeholder="Carga mínima"
                                style={{
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e0',
                                    width: '100%',
                                    fontSize: '15px',
                                    backgroundColor: '#fff',
                                    minHeight: '3rem',
                                    padding: '0 0.8rem',
                                    marginTop: '0.3rem',
                                }}
                            />
                        </Box>
                    </Flex>

                    {/* Botões de Download e Paginação no topo */}
                    {!internalLoading && totalCount > 0 && (
                        <Flex justify="space-between" align="center" mb={6} direction={{ base: 'column', lg: 'row' }} gap={4}>
                            {/* Paginação */}
                            {totalPages > 1 ? (
                                <Flex align="center" gap={4}>
                                    <ChakraButton
                                        isDisabled={page === 1}
                                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                        colorScheme="blue"
                                        variant="outline"
                                        size="md"
                                    >
                                        Anterior
                                    </ChakraButton>
                                    <Text fontSize="lg" fontWeight="medium" color="gray.600">
                                        Página {page} de {totalPages} ({totalCount} participantes)
                                    </Text>
                                    <ChakraButton
                                        isDisabled={page >= totalPages}
                                        onClick={() => setPage((prev) => prev + 1)}
                                        colorScheme="blue"
                                        variant="outline"
                                        size="md"
                                    >
                                        Próxima
                                    </ChakraButton>
                                </Flex>
                            ) : (
                                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                                    {totalCount} participantes
                                </Text>
                            )}

                            {/* Botões de Download */}
                            <Flex gap={3}>
                                <CustomButton
                                    style={{
                                        backgroundColor: COLORS.success,
                                        margin: 0,
                                    }}
                                    onClick={() => handleDownloadReport(false)}
                                    disabled={downloading}
                                    noLoading={false}
                                >
                                    {downloading ? 'Baixando...' : 'Baixar resumo'}
                                </CustomButton>
                                <CustomButton
                                    style={{
                                        backgroundColor: COLORS.success,
                                        margin: 0,
                                    }}
                                    onClick={() => handleDownloadReport(true)}
                                    disabled={downloading}
                                    noLoading={false}
                                >
                                    {downloading ? 'Baixando...' : 'Baixar detalhado'}
                                </CustomButton>
                            </Flex>
                        </Flex>
                    )}

                    {internalLoading ? (
                        <Flex justify="center" p={10}>
                            <LoadingSpinner />
                        </Flex>
                    ) : internalError ? (
                        <ErrorMessage>{internalError}</ErrorMessage>
                    ) : (
                        <Box border="1px solid" borderColor="gray.100" p={4} borderRadius="lg" bg="gray.50">
                            {modalOpen ? renderParticipatingUsers(internalUsers) : null}
                        </Box>
                    )}
                </ModalBody>

                <ModalFooter borderTop="1px solid" borderColor="gray.100" py={4} justifyContent="flex-end">
                    {!internalLoading && totalCount > 0 && (
                        <Flex gap={3} w="100%" justify="flex-end">
                            <CustomButton
                                style={{
                                    backgroundColor: COLORS.success,
                                    margin: 0,
                                }}
                                onClick={() => handleDownloadReport(false)}
                                disabled={downloading}
                                noLoading={false}
                            >
                                {downloading ? 'Baixando...' : 'Baixar resumo'}
                            </CustomButton>
                            <CustomButton
                                style={{
                                    backgroundColor: COLORS.success,
                                    margin: 0,
                                }}
                                onClick={() => handleDownloadReport(true)}
                                disabled={downloading}
                                noLoading={false}
                            >
                                {downloading ? 'Baixando...' : 'Baixar detalhado'}
                            </CustomButton>
                        </Flex>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

ShowParticipatingUsers.propTypes = {
    modalOpen: PropTypes.bool.isRequired,
    setModalOpen: PropTypes.func.isRequired,
    event: PropTypes.any.isRequired,
};

export default ShowParticipatingUsers;
